/**
 * Flatten a navigation tree into linear page order for prev/next navigation.
 */

import type { TreeNode } from '@/services/docsApi';
import type { SummaryEntry } from './summaryParser';
import { flattenSummary } from './summaryParser';

export interface PageLink {
  title: string;
  path: string;
}

/**
 * Get linear page order from SUMMARY.md entries or file tree.
 */
export function getPageOrder(
  summaryEntries: SummaryEntry[] | null,
  tree: TreeNode | null
): PageLink[] {
  if (summaryEntries && summaryEntries.length > 0) {
    return flattenSummary(summaryEntries);
  }

  if (!tree) return [];
  return flattenTree(tree);
}

function flattenTree(node: TreeNode): PageLink[] {
  const pages: PageLink[] = [];

  function walk(n: TreeNode) {
    if (n.type === 'file') {
      pages.push({ title: n.title || n.name, path: n.path });
    }
    if (n.children) {
      // Dirs first (sorted), then files (sorted)
      const sorted = [...n.children].sort((a, b) => {
        if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      for (const child of sorted) {
        walk(child);
      }
    }
  }

  walk(node);
  return pages;
}

/**
 * Get previous and next pages relative to the current path.
 */
export function getPrevNext(
  pages: PageLink[],
  currentPath: string
): { prev: PageLink | null; next: PageLink | null } {
  const index = pages.findIndex((p) => p.path === currentPath);
  if (index === -1) return { prev: null, next: null };
  return {
    prev: index > 0 ? pages[index - 1] : null,
    next: index < pages.length - 1 ? pages[index + 1] : null,
  };
}
